import React, { useEffect, useMemo, useState } from "react";
import dataService from "../../services/dataService";
import { useNavigate } from "react-router-dom";
import { getCurrentUser } from "../../auth";
 
import { getPayments as fetchPayments } from "../../services/transactionsService";
import { db } from "../../services/firebase";
import { collection, onSnapshot, addDoc } from "firebase/firestore";
import { getAllStudents } from "../../services/studentsService";
import { chartOfAccounts as defaultAccounts } from "../../data/chartOfAccounts";
import { parseMoneyAmount } from "../../utils/feeCalculator";
export default function CashFlow() {
  const [cashFlows, setCashFlows] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [summaryDate, setSummaryDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [revenueScope, setRevenueScope] = useState("day");
  const [revenueDate, setRevenueDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [revenueMonth, setRevenueMonth] = useState(() => String(new Date().getMonth() + 1).padStart(2, "0"));
  const [revenueYear, setRevenueYear] = useState(() => String(new Date().getFullYear()));
  const [paymentsList, setPaymentsList] = useState([]);
  const [previewCampus, setPreviewCampus] = useState("");
  const [splitByCampus, setSplitByCampus] = useState(false);
  const [ledgerCampus, setLedgerCampus] = useState("");
  const [campusScope, setCampusScope] = useState("");
  const [form, setForm] = useState({
    type: "expense",
    subType: "tuition",
    campus: "",
    studentId: "",
    category: "",
    description: "",
    method: "Cash",
    reference: "",
    remarks: "",
    amount: "",
    date: "",
    accountId: ""
  });
  const navigate = useNavigate();

  // Load Chart of Accounts (Firestore → localStorage → defaults)
  const [remoteAccounts, setRemoteAccounts] = useState([]);
  useEffect(() => {
    try {
      const unsub = onSnapshot(
        collection(db, "accounts"),
        (snap) => {
          const rows = snap.docs.map(d => ({ id: Number(d.data().id ?? d.id), ...d.data() }));
          if (rows.length) setRemoteAccounts(rows);
        },
        (err) => { setRemoteAccounts(prev => prev); }
      );
      return () => { try { unsub(); } catch (e) { setRemoteAccounts(prev => prev); } };
    } catch (e) { setRemoteAccounts(prev => prev); }
  }, []);
  const accounts = useMemo(() => {
    const byKey = new Map();
    const put = (arr) => {
      (arr || []).forEach(a => {
        const key = `${String(a.name || "").toLowerCase()}|${String(a.type || "").toLowerCase()}`;
        if (!byKey.has(key)) byKey.set(key, a);
      });
    };
    try {
      const raw = localStorage.getItem("accounts");
      const local = raw ? JSON.parse(raw) : [];
      put(remoteAccounts || []);
      put(local);
      put(defaultAccounts);
    } catch {
      put(remoteAccounts || []);
      put(defaultAccounts);
    }
    return Array.from(byKey.values()).sort((a,b)=>String(a.name).localeCompare(String(b.name)));
  }, [remoteAccounts]);
  const incomeAccounts = accounts.filter(a => a.type === "Income");
  const expenseAccounts = accounts.filter(a => a.type === "Expense");
  const campuses = ["bulacan", "gapan"].filter((v, i, arr) => arr.indexOf(v) === i);

  useEffect(() => {
    // Collections are derived from Payments; keep legacy collections for backward-compat totals if present
    setCashFlows([]);
    setExpenses(dataService.getAllExpenses());
    // Live expenses from Firestore for cross-device visibility
    try {
      const unsub = onSnapshot(
        collection(db, "expenses"),
        (snap) => {
          const cloud = snap.docs.map(d => ({ id: d.id, ...d.data() }));
          const local = dataService.getAllExpenses() || [];
          const key = (x) => `${x.date}|${x.campus}|${x.amount}|${x.description}|${x.accountId}`;
          const map = new Map();
          (local || []).forEach(x => map.set(key(x), x));
          (cloud || []).forEach(x => map.set(key(x), x));
          setExpenses(Array.from(map.values()));
        },
        (err) => {
          const local = dataService.getAllExpenses() || [];
          setExpenses(local);
        }
      );
      return () => {
        try { unsub(); } catch (e) { /* no-op */ }
      };
    } catch (e) {
      const local = dataService.getAllExpenses() || [];
      setExpenses(local);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const rows = await fetchPayments();
        setPaymentsList(Array.isArray(rows) ? rows : []);
      } catch {
        setPaymentsList([]);
      }
    })();
    const unsub = onSnapshot(
      collection(db, "payments"),
      (snap) => {
        const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setPaymentsList(rows);
      },
      (err) => {
        setPaymentsList(prev => prev);
      }
    );
    return () => unsub();
  }, []);

  // Collections is the single source of truth for daily cash flow.

  const handleSubmit = async e => {
    e.preventDefault();
    const selectedAccount = form.accountId ? accounts.find(a => a.id === Number(form.accountId)) : null;
    const payload = {
      ...form,
      category: selectedAccount ? selectedAccount.name : form.category,
      accountId: selectedAccount ? selectedAccount.id : null,
      campus: (form.campus || userCampus || "").toLowerCase().includes("gapan") ? "gapan"
        : (String(form.campus || userCampus || "").toLowerCase().includes("bulacan") ? "bulacan" : (form.campus || userCampus || "")),
      date: form.date || summaryDate
    };
    dataService.saveExpense(payload); // local fallback
    try {
      await addDoc(collection(db, "expenses"), {
        ...payload,
        amount: Number(payload.amount || 0),
        createdAt: new Date().toISOString(),
        createdBy: getCurrentUser()?.uid || ""
      });
    } catch {
      // ignore write error; local fallback still retains entry on this device
    }
    setExpenses(prev => {
      const key = (x) => `${x.date}|${x.campus}|${x.amount}|${x.description}|${x.accountId}`;
      const map = new Map((prev || []).map(x => [key(x), x]));
      map.set(key(payload), payload);
      return Array.from(map.values());
    });
    setForm({ type: "expense", subType: "tuition", campus: userCampus || "", studentId: "", category: "", description: "", method: "Cash", reference: "", remarks: "", amount: "", date: "", accountId: "" });
  };

  const currentUser = getCurrentUser();
  const userCampus = currentUser?.campus;
  const isAdmin = currentUser?.role === "admin" || currentUser?.role === "superadmin" || userCampus === "all";
  const normCampus = (v) => {
    const x = String(v || "").toLowerCase().trim();
    if (x.includes("gapan")) return "gapan";
    if (x.includes("bulacan")) return "bulacan";
    return x;
  };
  const studentsMap = useMemo(() => {
    try {
      const list = getAllStudents();
      const map = new Map();
      (list || []).forEach(s => map.set(String(s.id), normCampus(s.campus)));
      return map;
    } catch {
      return new Map();
    }
  }, []);
  const visibleCollections = isAdmin
    ? paymentsList.filter(i => {
        if (!campusScope) return true;
        const pc = normCampus(i.campus);
        if (pc) return pc === normCampus(campusScope);
        const sc = studentsMap.get(String(i.studentId || ""));
        return sc === normCampus(campusScope);
      })
    : paymentsList.filter(i => {
        const pc = normCampus(i.campus);
        if (pc) return pc === normCampus(userCampus);
        const sc = studentsMap.get(String(i.studentId || ""));
        return sc === normCampus(userCampus);
      });
  const visibleExpenses = isAdmin
    ? expenses.filter(i => {
        if (!campusScope) return true;
        const ic = normCampus(i.campus);
        return !ic || ic === normCampus(campusScope);
      })
    : expenses.filter(i => normCampus(i.campus) === normCampus(userCampus));

  async function syncLocalExpensesToCloud() {
    try {
      const locals = dataService.getAllExpenses() || [];
      const done = new Set();
      for (const e of locals) {
        const key = `${e.date}|${e.campus}|${e.amount}|${e.description}|${e.accountId}`;
        if (done.has(key)) continue;
        done.add(key);
        try {
          await addDoc(collection(db, "expenses"), {
            ...e,
            amount: Number(e.amount || 0),
            createdAt: new Date().toISOString(),
            createdBy: getCurrentUser()?.uid || ""
          });
        } catch (err) {
          // skip duplicates or network errors silently
        }
      }
    } catch (err) {
      // ignore sync errors; user can retry
    }
  }
  const toDate = (v) => {
    if (!v) return null;
    if (v instanceof Date) return v;
    if (typeof v?.toDate === "function") return v.toDate();
    const d = new Date(v);
    return Number.isFinite(d.getTime()) ? d : null;
  };
  const ymd = (v) => {
    const d = toDate(v);
    if (d) return d.toISOString().slice(0, 10);
    return String(v || "").slice(0, 10);
  };
  const collectionsForDate = useMemo(
    () => visibleCollections.filter(p => String(p.date || "").slice(0, 10) === summaryDate),
    [visibleCollections, summaryDate]
  );
  const expensesForDate = useMemo(
    () => visibleExpenses.filter(e => String(e.date || "").slice(0, 10) === summaryDate),
    [visibleExpenses, summaryDate]
  );
  const totalCollections = useMemo(
    () => collectionsForDate.reduce((sum, item) => sum + parseMoneyAmount(item.amount), 0),
    [collectionsForDate]
  );
  const totalExpenses = useMemo(
    () => expensesForDate.reduce((sum, item) => sum + parseMoneyAmount(item.amount), 0),
    [expensesForDate]
  );
  const revenue = useMemo(() => totalCollections - totalExpenses, [totalCollections, totalExpenses]);

  const totalCollectionsAll = useMemo(
    () => visibleCollections.reduce((sum, item) => sum + parseMoneyAmount(item.amount), 0),
    [visibleCollections]
  );
  const totalExpensesAll = useMemo(
    () => visibleExpenses.reduce((sum, item) => sum + parseMoneyAmount(item.amount), 0),
    [visibleExpenses]
  );

  const revenueCollections = useMemo(() => {
    const scope = String(revenueScope || "day");
    return visibleCollections.filter(p => {
      const d = toDate(p.date);
      if (!d) return false;
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const y = String(d.getFullYear());
      if (scope === "day") return ymd(d) === revenueDate;
      if (scope === "month") return y === revenueYear && m === revenueMonth;
      if (scope === "year") return y === revenueYear;
      return false;
    });
  }, [visibleCollections, revenueScope, revenueDate, revenueMonth, revenueYear]);

  const revenueExpenses = useMemo(() => {
    const scope = String(revenueScope || "day");
    return visibleExpenses.filter(e => {
      const d = toDate(e.date);
      if (!d) return false;
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const y = String(d.getFullYear());
      if (scope === "day") return ymd(d) === revenueDate;
      if (scope === "month") return y === revenueYear && m === revenueMonth;
      if (scope === "year") return y === revenueYear;
      return false;
    });
  }, [visibleExpenses, revenueScope, revenueDate, revenueMonth, revenueYear]);

  const revenueTotalCollections = useMemo(
    () => revenueCollections.reduce((sum, item) => sum + parseMoneyAmount(item.amount), 0),
    [revenueCollections]
  );
  const revenueTotalExpenses = useMemo(
    () => revenueExpenses.reduce((sum, item) => sum + parseMoneyAmount(item.amount), 0),
    [revenueExpenses]
  );
  const revenueNet = useMemo(
    () => revenueTotalCollections - revenueTotalExpenses,
    [revenueTotalCollections, revenueTotalExpenses]
  );
  // Preview of same-day collections combining Student Fees/Payments and Cash Flow entries
  const todaysByCampusCategories = useMemo(() => {
    const accountsMap = (() => {
      try {
        const raw = localStorage.getItem("accounts");
        const list = raw ? JSON.parse(raw) : [];
        const map = {};
        (list || []).forEach(a => { map[a.id] = a; });
        return map;
      } catch {
        return {};
      }
    })();
    const payments = (isAdmin ? paymentsList : paymentsList.filter(p => {
      const pc = normCampus(p.campus);
      if (pc) return pc === normCampus(userCampus);
      const sc = studentsMap.get(String(p.studentId || ""));
      return sc === normCampus(userCampus);
    }))
      .filter(p => p.date === summaryDate);
    const out = {};
    const add = (campus, accountId, categoryName, amount) => {
      const acc = accountId ? accountsMap[Number(accountId)] : null;
      const name = (categoryName || (acc ? acc.name : "") || "Uncategorized").toString();
      if (!out[campus]) out[campus] = { total: 0, cats: {} };
      out[campus].cats[name] = Number(out[campus].cats[name] || 0) + Number(amount || 0);
      out[campus].total += Number(amount || 0);
    };
    payments
      .filter(p => {
        if (!isAdmin || !campusScope) return true;
        const c = normCampus(p.campus) || studentsMap.get(String(p.studentId || ""));
        return normCampus(c) === normCampus(campusScope);
      })
      .forEach(p => {
        const c = normCampus(p.campus) || studentsMap.get(String(p.studentId || "")) || "unknown";
        add(c, p.accountId, p.category, p.amount);
      });
    // Ensure known campuses appear for admin even with zero totals
    if (isAdmin) {
      if (!out["gapan"]) out["gapan"] = { total: 0, cats: {} };
      if (!out["bulacan"]) out["bulacan"] = { total: 0, cats: {} };
    }
    return out;
  }, [paymentsList, cashFlows, summaryDate, isAdmin, userCampus, studentsMap]);
  function useCampusAccount(campus, accountId) {
    setForm(f => ({ ...f, type: "collection", accountId: String(accountId || ""), campus, date: summaryDate }));
  }

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center gap-3">
          <h2 className="mb-0">Daily Cash Flow Records</h2>
          {isAdmin && (
            <div className="d-flex align-items-center gap-2">
              <label className="form-label mb-0">Campus</label>
              <select
                className="form-select"
                style={{ width: 160 }}
                value={campusScope}
                onChange={e => {
                  setCampusScope(e.target.value);
                  setPreviewCampus(e.target.value || "");
                  setLedgerCampus(e.target.value || "");
                }}
                title="Scope all sections by campus"
              >
                <option value="">All</option>
                <option value="gapan">gapan</option>
                <option value="bulacan">bulacan</option>
              </select>
            </div>
          )}
        </div>
        <div className="d-flex gap-2">
          {isAdmin && (
            <button className="btn btn-outline-secondary btn-sm" onClick={syncLocalExpensesToCloud} title="Upload any local expenses to Firestore for cross-device visibility">
              Sync Local Expenses
            </button>
          )}
          <button className="btn btn-secondary btn-sm" onClick={() => navigate(-1)}>
            {"\u20B1"} Back
          </button>
        </div>
      </div>

      <div className="card mb-3 shadow-sm">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="mb-0">Today's Collections by Campus & Account</h5>
            <div className="d-flex gap-2">
              <input type="date" className="form-control" style={{ width: 180 }} value={summaryDate} onChange={e => setSummaryDate(e.target.value)} />
              <select
                className="form-select"
                style={{ width: 180 }}
                value={previewCampus}
                onChange={e => setPreviewCampus(e.target.value)}
                disabled={!isAdmin}
                title={isAdmin ? "Filter by campus" : "Campus is limited by your access"}
              >
                <option value="">All Campuses</option>
                {Object.keys(todaysByCampusCategories).sort((a,b)=>a.localeCompare(b)).map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <div className="form-check form-switch d-flex align-items-center">
                <input className="form-check-input" type="checkbox" id="splitByCampus" checked={splitByCampus} onChange={e => setSplitByCampus(e.target.checked)} />
                <label className="form-check-label ms-2" htmlFor="splitByCampus">Split by Campus</label>
              </div>
            </div>
          </div>
          <div className="text-muted small mb-2">
            Preview totals combine Student Fees/Payments and Cash Flow entries. Official collections are recorded below.
          </div>
          {(() => {
            const entries = Object.entries(todaysByCampusCategories)
              .filter(([c]) => !previewCampus || c === previewCampus)
              .sort(([a],[b]) => a.localeCompare(b));
            if (entries.length === 0) {
              return (
                <div className="table-responsive">
                  <table className="table table-sm table-bordered mb-0">
                    <tbody>
                      <tr><td className="text-center text-muted">No collections for selected date.</td></tr>
                    </tbody>
                  </table>
                </div>
              );
            }
            if (splitByCampus) {
              return (
                <div className="row g-3">
                  {entries.map(([campus, v]) => {
                    const rows = Object.entries(v.cats || {}).map(([name, amount]) => ({ name, amount: Number(amount || 0) })).filter(r => r.amount > 0);
                    return (
                      <div className="col-md-6" key={campus}>
                        <div className="table-responsive">
                          <table className="table table-sm table-bordered mb-0">
                            <thead className="table-light">
                              <tr>
                                <th colSpan="3">{campus}</th>
                              </tr>
                              <tr>
                                <th>Account</th>
                                <th className="text-end">Total ({"\u20B1"})</th>
                                <th className="text-center" style={{ width: 200 }}>Quick Fill</th>
                              </tr>
                            </thead>
                            <tbody>
                              {rows.map(r => (
                                <tr key={campus + "-" + r.name}>
                                  <td>{r.name}</td>
                                  <td className="text-end fw-bold">{"\u20B1"} {r.amount.toLocaleString()}</td>
                                  <td className="text-center">
                                    <button
                                      className="btn btn-sm btn-outline-primary"
                                      onClick={() => useCampusAccount(campus, Object.values(accounts).find(a => a.name === r.name)?.id)}
                                    >
                                      Use {r.name}
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr className="fw-bold bg-light">
                                <td className="text-end">Total</td>
                                <td className="text-end">{"\u20B1"} {Number(v.total || 0).toLocaleString()}</td>
                                <td></td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            }
            // Single table (filtered/sorted)
            return (
              <div className="table-responsive">
                <table className="table table-sm table-bordered mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Campus</th>
                      <th>Account</th>
                      <th className="text-end">Total ({"\u20B1"})</th>
                      <th className="text-center" style={{ width: 220 }}>Quick Fill</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.flatMap(([campus, v]) => {
                      const rows = Object.entries(v.cats || {}).map(([name, amount]) => ({ name, amount: Number(amount || 0) }));
                      return rows
                        .filter(r => r.amount > 0)
                        .map(r => (
                          <tr key={campus + "-" + r.name}>
                            <td>{campus}</td>
                            <td>{r.name}</td>
                            <td className="text-end fw-bold">{"\u20B1"} {r.amount.toLocaleString()}</td>
                            <td className="text-center">
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => useCampusAccount(campus, Object.values(accounts).find(a => a.name === r.name)?.id)}
                              >
                                Use {r.name}
                              </button>
                            </td>
                          </tr>
                        ));
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="fw-bold bg-light">
                      <td colSpan="2" className="text-end">Grand Total</td>
                      <td className="text-end">
                        {"\u20B1"} {entries.reduce((sum, [,v]) => sum + Number(v.total || 0), 0).toLocaleString()}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            );
          })()}
        </div>
      </div>

      <div className="row g-3 mb-3">
        <div className="col-md-4">
          <div className="card border-0 shadow-sm p-3" style={{ background: "#e8f5e9" }}>
            <div className="text-muted">Total Collections ({summaryDate})</div>
            <div className="display-6 fw-bold text-success">₱{totalCollections.toLocaleString()}</div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card border-0 shadow-sm p-3" style={{ background: "#ffebee" }}>
            <div className="text-muted">Total Expenses ({summaryDate})</div>
            <div className="display-6 fw-bold text-danger">₱{totalExpenses.toLocaleString()}</div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card border-0 shadow-sm p-3" style={{ background: "#e3f2fd" }}>
            <div className="text-muted">Net Cash Flow ({summaryDate})</div>
            <div className="display-6 fw-bold text-primary">₱{revenue.toLocaleString()}</div>
          </div>
        </div>
      </div>

      <div className="card mb-3 shadow-sm">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div className="d-flex align-items-center gap-3">
              <h5 className="mb-0">Daily Transaction Ledger (Columnar)</h5>
              {isAdmin && (
                <div className="d-flex align-items-center gap-2">
                  <label className="form-label mb-0">Campus</label>
                  <select className="form-select" style={{ width: 160 }} value={ledgerCampus} onChange={e => setLedgerCampus(e.target.value)}>
                    <option value="">All</option>
                    <option value="gapan">gapan</option>
                    <option value="bulacan">bulacan</option>
                  </select>
                </div>
              )}
            </div>
            <input type="date" className="form-control" style={{ width: 180 }} value={summaryDate} onChange={e => setSummaryDate(e.target.value)} />
          </div>
          <div className="table-responsive">
            <table className="table table-sm table-striped table-bordered">
              <thead className="table-light">
                <tr>
                  <th style={{width: 110}}>Date</th>
                  <th style={{width: 120}}>OR No</th>
                  <th>Payer</th>
                  <th>Campus</th>
                  <th>Account/Category</th>
                  <th>Description</th>
                  <th>Payment Method</th>
                  <th className="text-end" style={{width: 120}}>Amount (₱)</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const accountsMap = (() => {
                    try {
                      const raw = localStorage.getItem("accounts");
                      const list = raw ? JSON.parse(raw) : [];
                      const map = {};
                      (list || []).forEach(a => { map[a.id] = a; });
                      return map;
                    } catch {
                      return {};
                    }
                  })();
                  const rows = (isAdmin ? paymentsList : paymentsList.filter(p => (p.campus || "") === userCampus))
                    .filter(p => (p.date || "") === summaryDate)
                    .filter(p => {
                      if (!isAdmin || !ledgerCampus) return true;
                      const c = normCampus(p.campus) || studentsMap.get(String(p.studentId || ""));
                      return normCampus(c) === normCampus(ledgerCampus);
                    })
                    .sort((a, b) => String(a.reference || "").localeCompare(String(b.reference || "")));
                  if (rows.length === 0) {
                    return (
                      <tr><td colSpan="8" className="text-center text-muted">No transactions recorded for selected date.</td></tr>
                    );
                  }
                  const fmtMethod = (p) => {
                    const m = String(p.method || "").toLowerCase();
                    if (m.includes("gcash") || m.includes("paymaya") || m.includes("e-wallet") || m.includes("ewallet")) return `E‑Wallet (${p.method})`;
                    if (m.includes("bank")) return "Bank Transfer";
                    if (m.includes("check") || m.includes("cheque")) return "Check";
                    if (m.includes("pos") || m.includes("card") || m.includes("visa") || m.includes("mastercard")) return "POS Card";
                    if (m.includes("online")) return "Online";
                    return p.method || "";
                  };
                  return rows.map((p, idx) => {
                    const acc = accountsMap[p.accountId] || null;
                    const cat = p.category || (acc ? acc.name : "");
                    return (
                      <tr key={idx}>
                        <td>{p.date}</td>
                        <td>{p.reference}</td>
                        <td>{p.payer || ""}</td>
                        <td>{p.campus || ""}</td>
                        <td>{cat}</td>
                        <td>{p.description || ""}</td>
                        <td>{fmtMethod(p)}</td>
                        <td className="text-end">₱{Number(p.amount || 0).toLocaleString()}</td>
                      </tr>
                    );
                  });
                })()}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="card mb-4 shadow-sm">
        <div className="card-body">
          <form onSubmit={handleSubmit} className="row g-3">
            <div className="col-md-3">
              <label className="form-label">Type</label>
              <select className="form-select" value={form.type} onChange={e => setForm({ ...form, type: e.target.value, accountId: "", category: "" })}>
                <option value="expense">Expense</option>
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Campus</label>
              <select className="form-select" value={form.campus} onChange={e => setForm({ ...form, campus: e.target.value })} required>
                <option value="">Select Campus</option>
                {campuses.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            
            <div className="col-md-3">
              <label className="form-label">Account</label>
              <select
                className="form-select"
                value={form.accountId}
                onChange={e => setForm({ ...form, accountId: e.target.value })}
                required
              >
                <option value="">Select Account</option>
                {expenseAccounts.map(acc => (
                  <option key={acc.id} value={acc.id}>{acc.name}</option>
                ))}
              </select>
            </div>
            
            {form.type === "expense" && (
              <div className="col-md-3">
                <label className="form-label">Description</label>
                <input type="text" className="form-control" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} required />
              </div>
            )}
            
            <div className="col-md-2">
              <label className="form-label">Amount</label>
              <input type="number" className="form-control" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} required />
            </div>
            <div className="col-md-2">
              <label className="form-label">Date</label>
              <input type="date" className="form-control" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} required />
            </div>
            <div className="col-md-4">
              <label className="form-label">Remarks</label>
              <input className="form-control" value={form.remarks} onChange={e => setForm({ ...form, remarks: e.target.value })} />
            </div>
            <div className="col-md-2 d-flex align-items-end">
              <button type="submit" className="btn btn-primary w-100">Save</button>
            </div>
          </form>
        </div>
      </div>

      <div className="mb-4">
        <div className="card shadow-sm">
          <div className="card-header bg-success text-white fw-bold d-flex justify-content-between align-items-center">
            <span>Collections (from Payments)</span>
            <span className="fw-bold">₱{totalCollectionsAll.toLocaleString()}</span>
          </div>
            <div className="card-body p-0">
              <table className="table table-striped mb-0">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Campus</th>
                    <th>Category</th>
                        <th>OR Number</th>
                        <th>Description</th>
                    <th>Amount ({"\u20B1"})</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleCollections.length === 0 ? (
                    <tr>
                          <td colSpan="6" className="text-center text-muted">No collections recorded.</td>
                    </tr>
                  ) : (
                    visibleCollections
                      .slice()
                      .sort((a, b) => String(b.date || "").localeCompare(String(a.date || "")) || String(b.reference || "").localeCompare(String(a.reference || "")))
                      .map((item, idx) => (
                      <tr key={idx}>
                        <td>{item.date}</td>
                        <td>{item.campus || ""}</td>
                        <td>{item.category}</td>
                            <td>{item.reference || ""}</td>
                            <td>{item.description || ""}</td>
                        <td>{"\u20B1"} {parseMoneyAmount(item.amount).toLocaleString()}</td>
                      </tr>
                    ))
                  )}
                  <tr className="fw-bold bg-light">
                        <td colSpan="5" className="text-end">Total:</td>
                    <td>{"\u20B1"} {totalCollectionsAll.toLocaleString()}</td>
                  </tr>
                </tbody>
              </table>
            </div>
        </div>
      </div>

      <div className="mb-4">
        <div className="card shadow-sm">
          <div className="card-header bg-danger text-white fw-bold d-flex justify-content-between align-items-center">
            <span>Expenses</span>
            <span className="fw-bold">₱{totalExpensesAll.toLocaleString()}</span>
          </div>
          <div className="card-body p-0">
            <table className="table table-striped mb-0">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Campus</th>
                  <th>Category</th>
                  <th>Description</th>
                  <th>Amount ({"\u20B1"})</th>
                </tr>
              </thead>
              <tbody>
                {visibleExpenses.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center text-muted">No expenses recorded.</td>
                  </tr>
                ) : (
                  visibleExpenses
                    .slice()
                    .sort((a, b) => String(b.date || "").localeCompare(String(a.date || "")))
                    .map((item, idx) => (
                    <tr key={idx}>
                      <td>{item.date}</td>
                      <td>{item.campus || ""}</td>
                      <td>{item.category}</td>
                      <td>{item.description}</td>
                      <td>{"\u20B1"} {parseMoneyAmount(item.amount).toLocaleString()}</td>
                    </tr>
                  ))
                )}
                <tr className="fw-bold bg-light">
                  <td colSpan="4" className="text-end">Total:</td>
                  <td>{"\u20B1"} {totalExpensesAll.toLocaleString()}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <h4 className="fw-bold mb-3">Revenue Report</h4>
          <div className="row g-2 align-items-end mb-3">
            <div className="col-md-3">
              <label className="form-label">Filter</label>
              <select className="form-select" value={revenueScope} onChange={e => setRevenueScope(e.target.value)}>
                <option value="day">Per Day</option>
                <option value="month">Per Month</option>
                <option value="year">Per Year</option>
              </select>
            </div>
            {revenueScope === "day" && (
              <div className="col-md-3">
                <label className="form-label">Date</label>
                <input type="date" className="form-control" value={revenueDate} onChange={e => setRevenueDate(e.target.value)} />
              </div>
            )}
            {revenueScope === "month" && (
              <>
                <div className="col-md-2">
                  <label className="form-label">Month</label>
                  <select className="form-select" value={revenueMonth} onChange={e => setRevenueMonth(e.target.value)}>
                    {Array.from({ length: 12 }).map((_, i) => {
                      const m = String(i + 1).padStart(2, "0");
                      return <option key={m} value={m}>{m}</option>;
                    })}
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label">Year</label>
                  <input className="form-control" value={revenueYear} onChange={e => setRevenueYear(e.target.value)} />
                </div>
              </>
            )}
            {revenueScope === "year" && (
              <div className="col-md-2">
                <label className="form-label">Year</label>
                <input className="form-control" value={revenueYear} onChange={e => setRevenueYear(e.target.value)} />
              </div>
            )}
          </div>
          <div className="d-flex flex-wrap gap-4">
            <div>
              <span className="fw-bold">Total Collections:</span>
              <span className="ms-2 text-success">{"\u20B1"} {revenueTotalCollections.toLocaleString()}</span>
            </div>
            <div>
              <span className="fw-bold">Total Expenses:</span>
              <span className="ms-2 text-danger">{"\u20B1"} {revenueTotalExpenses.toLocaleString()}</span>
            </div>
            <div>
              <span className="fw-bold">Net Cash Flow:</span>
              <span className="ms-2 text-primary">{"\u20B1"} {revenueNet.toLocaleString()}</span>
            </div>
            {/* You can add annual or filtered reports here */}
          </div>
        </div>
      </div>
    </div>
  );
}
